/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { App } from './App';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Route,
    RouterProvider,
} from 'react-router-dom';
import { AppRouter } from './router';
import { env } from './config/env';
import { withModeTitlePrefix } from './utils/appMode';
import { applyStoredDocumentTheme } from './layout/components/DashboardThemeToggle';

// A development build names its mode in the tab title, e.g. `[DEV] Bos App`,
// so tabs on different APIs are told apart at a glance (BOS-236).
document.title = withModeTitlePrefix(document.title, env.modeDisplay);

// The stored light/dark choice, before the first render: the shell's toggles
// apply it too, but full-screen pages have no shell.
applyStoredDocumentTheme();

const elem = document.getElementById('root');

if (!elem) {
    throw new Error('Root element not found');
}
const router = createBrowserRouter(
    createRoutesFromElements(<Route element={<App />}>{AppRouter}</Route>),
);

const app = (
    <StrictMode>
        <RouterProvider router={router} />
    </StrictMode>
);

const bosWindow = window as Window & { __bosRoot?: Root };

/**
 * React recovers from an error thrown during a concurrent render by throwing
 * the attempt away and re-rendering synchronously. The user sees nothing; the
 * console gets a minified "Minified React error #520" with no component in it.
 * The audit found nine of those across one navigation sequence and could not
 * say what caused any of them, because the only build anyone runs is the
 * minified one.
 *
 * `onRecoverableError` is the hook React provides for exactly this: it receives
 * the error *and* a `componentStack`, which is the part the minified message
 * throws away. Logging both means the next person to see one starts from a
 * component name instead of an error number. This stays in permanently — it
 * costs nothing while nothing is wrong, and there is no other way to find out.
 */
bosWindow.__bosRoot ??= createRoot(elem, {
    onRecoverableError: (error, errorInfo) => {
        console.error(
            '[react] recovered from a render error',
            error,
            errorInfo.componentStack,
        );
    },
});
bosWindow.__bosRoot.render(app);
